/*
  Franchise application form (sections/franchise.liquid).

  The form goes to the reviews service through the app proxy /apps/reviews/franchise
  as multipart/form-data, so the attached file travels with the fields. Shopify
  signs the request; the service checks the fields, mails the application to the
  franchise address and keeps a copy.

  Field checks here mirror the service, so obvious mistakes don't need a round trip.
*/
if (!customElements.get('franchise-form')) {
  customElements.define(
    'franchise-form',
    class FranchiseForm extends HTMLElement {
      static FILE_MAX_BYTES = 10 * 1024 * 1024;
      static FILE_EXTENSIONS = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'webp'];

      connectedCallback() {
        this.endpoint = this.dataset.endpoint;
        const messages = this.querySelector('[data-franchise-messages]');
        this.messages = messages ? JSON.parse(messages.textContent) : {};

        this.form = this.querySelector('[data-franchise-form]');
        this.status = this.querySelector('[data-franchise-status]');
        this.button = this.querySelector('[data-franchise-submit]');

        this.form?.addEventListener('submit', (event) => this.submit(event));
        // Field errors disappear as soon as the field is corrected
        this.form?.addEventListener('input', (event) => {
          const field = event.target.closest('[data-field]')?.dataset.field ?? event.target.name;
          this.setFieldError(field, false);
        });
        this.form?.addEventListener('change', (event) => {
          if (event.target.name === 'file' || event.target.name === 'consent') this.setFieldError(event.target.name, false);
        });
      }

      showStatus(key) {
        this.status.textContent = this.messages[key] ?? this.messages.error;
        this.status.dataset.state = key;
        this.status.hidden = false;
      }

      setFieldError(field, visible) {
        const error = this.form.querySelector(`[data-error-for="${field}"]`);
        if (error) error.hidden = !visible;
        this.form.querySelector(`[data-field="${field}"]`)?.classList.toggle('franchise__field--invalid', visible);
      }

      validate() {
        const elements = this.form.elements;
        const length = (text) => [...String(text ?? '').trim()].length;
        const errors = [];

        if (length(elements.name.value) < 2 || length(elements.name.value) > 80) errors.push('name');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(elements.email.value.trim())) errors.push('email');
        const phone = elements.phone.value.trim();
        if (!/^\+?[\d\s().-]+$/.test(phone) || phone.replace(/\D/g, '').length < 5 || length(phone) > 40) errors.push('phone');
        if (length(elements.country.value) < 2 || length(elements.country.value) > 80) errors.push('country');
        if (length(elements.city.value) < 2 || length(elements.city.value) > 80) errors.push('city');
        if (length(elements.company.value) > 120) errors.push('company');
        if (length(elements.location.value) > 200) errors.push('location');
        if (length(elements.experience.value) > 3000) errors.push('experience');
        if (length(elements.message.value) > 3000) errors.push('message');
        if (!elements.consent.checked) errors.push('consent');

        const file = elements.file.files[0];
        if (file) {
          const extension = (file.name.match(/\.([A-Za-z0-9]{1,8})$/) || [])[1]?.toLowerCase() ?? '';
          if (!FranchiseForm.FILE_EXTENSIONS.includes(extension) || file.size === 0 || file.size > FranchiseForm.FILE_MAX_BYTES) {
            errors.push('file');
          }
        }
        return errors;
      }

      async submit(event) {
        event.preventDefault();
        const fields = ['name', 'email', 'phone', 'country', 'city', 'company', 'location', 'experience', 'message', 'consent', 'file'];
        const errors = this.validate();
        fields.forEach((field) => this.setFieldError(field, errors.includes(field)));
        if (errors.length > 0) {
          this.status.hidden = true;
          this.form.querySelector(`[data-field="${errors[0]}"] input, [data-field="${errors[0]}"] textarea`)?.focus();
          return;
        }

        const body = new FormData(this.form);
        body.set('locale', this.dataset.locale || 'en');
        if (!this.form.elements.file.files[0]) body.delete('file');

        this.button.disabled = true;
        this.button.textContent = this.messages.sending;
        this.status.hidden = true;

        let response;
        try {
          response = await fetch(this.endpoint, { method: 'POST', body, credentials: 'same-origin', headers: { Accept: 'application/json' } });
        } catch {
          response = null;
        }
        // Without the app the proxy answers with an HTML 404 page, not JSON
        const data = response ? await response.json().catch(() => ({})) : {};

        this.button.disabled = false;
        this.button.textContent = this.messages.submit;

        if (response?.ok) {
          this.form.hidden = true;
          this.form.reset();
          this.showStatus('success');
          this.status.scrollIntoView({ block: 'center', behavior: 'smooth' });
          window.cosmogenAnalytics?.track('franchise_submit', { with_file: body.has('file') });
          return;
        }

        if (data.error === 'invalid_application' && Array.isArray(data.fields)) {
          data.fields.forEach((field) => this.setFieldError(field, true));
          this.showStatus('invalid');
          return;
        }
        if (response?.status === 429) return this.showStatus('too_many');
        if (response?.status === 413 || data.error === 'body_too_large') return this.showStatus('too_large');
        this.showStatus('error');
      }
    },
  );
}
